import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Sparkles, CheckCircle2, AlertCircle, XCircle, Calendar as CalendarIcon, Ban, Sun, Moon, ShieldCheck } from "lucide-react";
import ReserveDateModal from "./ReserveDateModal";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS = ["S","M","T","W","T","F","S"];

// Full literal Tailwind classes so JIT keeps them. NEVER interpolate badge classes.
const POPULAR_BADGE_CLASSES = {
 nye: {
 cell: "bg-gradient-to-br from-fuchsia-500/25 via-violet-500/20 to-amber-400/25 ring-2 ring-fuchsia-400/60 hover:from-fuchsia-500/40 hover:to-amber-400/40",
 dot: "bg-gradient-to-r from-fuchsia-500 to-amber-400",
 pill: "bg-gradient-to-r from-fuchsia-500 to-amber-400 text-white",
 banner: "bg-gradient-to-r from-fuchsia-600 via-violet-600 to-amber-500 text-white",
 legendDot: "bg-gradient-to-r from-fuchsia-500 to-amber-400",
 },
 "nye-eve": {
 cell: "bg-gradient-to-br from-violet-500/25 to-pink-500/25 ring-2 ring-violet-400/60 hover:from-violet-500/40 hover:to-pink-500/40",
 dot: "bg-gradient-to-r from-violet-500 to-pink-500",
 pill: "bg-gradient-to-r from-violet-500 to-pink-500 text-white",
 banner: "bg-gradient-to-r from-violet-600 to-pink-600 text-white",
 legendDot: "bg-gradient-to-r from-violet-500 to-pink-500",
 },
 christmas: {
 cell: "bg-gradient-to-br from-rose-500/25 to-emerald-500/25 ring-2 ring-rose-400/60 hover:from-rose-500/40 hover:to-emerald-500/40",
 dot: "bg-gradient-to-r from-rose-500 to-emerald-500",
 pill: "bg-gradient-to-r from-rose-500 to-emerald-500 text-white",
 banner: "bg-gradient-to-r from-rose-600 to-emerald-600 text-white",
 legendDot: "bg-gradient-to-r from-rose-500 to-emerald-500",
 },
 "boxing-day": {
 cell: "bg-gradient-to-br from-emerald-500/25 to-cyan-500/25 ring-2 ring-emerald-400/60 hover:from-emerald-500/40 hover:to-cyan-500/40",
 dot: "bg-gradient-to-r from-emerald-500 to-cyan-500",
 pill: "bg-gradient-to-r from-emerald-500 to-cyan-500 text-white",
 banner: "bg-gradient-to-r from-emerald-600 to-cyan-600 text-white",
 legendDot: "bg-gradient-to-r from-emerald-500 to-cyan-500",
 },
 vivid: {
 cell: "bg-gradient-to-br from-blue-500/25 via-purple-500/20 to-pink-500/25 ring-2 ring-blue-400/60 hover:from-blue-500/40 hover:to-pink-500/40",
 dot: "bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500",
 pill: "bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white",
 banner: "bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white",
 legendDot: "bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500",
 },
 "australia-day": {
 cell: "bg-gradient-to-br from-yellow-400/30 to-green-500/25 ring-2 ring-yellow-400/70 hover:from-yellow-400/50 hover:to-green-500/40",
 dot: "bg-gradient-to-r from-yellow-400 to-green-500",
 pill: "bg-gradient-to-r from-yellow-400 to-green-500 text-ink",
 banner: "bg-gradient-to-r from-yellow-500 to-green-600 text-white",
 legendDot: "bg-gradient-to-r from-yellow-400 to-green-500",
 },
};

// Returns { id, label, blurb } for popular events, or null.
function getPopularBadge(date) {
 const m = date.getMonth(); // 0-11
 const d = date.getDate();
 if (m === 11 && d === 31) return { id: "nye", label: "New Year's Eve · Fireworks Cruise", blurb: "Front-row 9pm & midnight fireworks, sells out 6+ months ahead." };
 if (m === 11 && d === 30) return { id: "nye-eve", label: "NYE Eve · Sydney Skyline Party", blurb: "Cocktail cruise the night before NYE, limited spots." };
 if (m === 11 && d === 25) return { id: "christmas", label: "Christmas Day Cruise", blurb: "Festive private charter with seasonal canapés." };
 if (m === 11 && d === 26) return { id: "boxing-day", label: "Boxing Day · Sydney, Hobart Start", blurb: "Watch the Sydney to Hobart yacht race start from the water." };
 if (m === 0 && d === 26) return { id: "australia-day", label: "Australia Day Cruise", blurb: "Best harbour views for fireworks, ferrython & flotilla." };
 // Vivid Sydney 2027 runs 28 May, 19 June.
 if ((m === 4 && d >= 28) || (m === 5 && d <= 19)) return { id: "vivid", label: "Vivid Sydney · Light Cruise", blurb: "Sail through the lit-up harbour during Vivid Sydney." };
 return null;
}

function statusFor(count) {
 if (count === 0) return { id: "available", label: "Available", icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300" };
 if (count <= 2) return { id: "limited", label: "Limited availability", icon: AlertCircle, cls: "bg-amber-100 text-amber-900 ring-1 ring-amber-300" };
 return { id: "full", label: "Fully booked", icon: XCircle, cls: "bg-rose-100 text-rose-800 ring-1 ring-rose-300" };
}

const UNAVAILABLE_STATUS = { id: "unavailable", label: "Unavailable", icon: Ban, cls: "bg-zinc-200 text-zinc-700 ring-1 ring-zinc-400" };
const PARTIAL_STATUS = { id: "partial", label: "Partially available", icon: AlertCircle, cls: "bg-amber-100 text-amber-900 ring-1 ring-amber-300" };

function toISO(date) {
 const y = date.getFullYear();
 const m = String(date.getMonth() + 1).padStart(2, "0");
 const d = String(date.getDate()).padStart(2, "0");
 return `${y}-${m}-${d}`;
}

function buildGrid(year, month) {
 const first = new Date(year, month, 1);
 const startDow = first.getDay();
 const daysInMonth = new Date(year, month + 1, 0).getDate();
 const cells = [];
 for (let i = 0; i < startDow; i++) cells.push(null);
 for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
 while (cells.length % 7 !== 0) cells.push(null);
 return cells;
}

export default function AvailabilityCalendar({ compact = false, onDateSelect, hideCTA = false, selectedISO = null }) {
 const today = useMemo(() => { const t = new Date(); t.setHours(0,0,0,0); return t; }, []);
 const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
 const [internalSelected, setInternalSelected] = useState(null);
 const selected = selectedISO ? new Date(selectedISO + "T00:00:00") : internalSelected;
 const setSelected = (d) => {
 setInternalSelected(d);
 if (onDateSelect) onDateSelect(d ? toISO(d) : null);
 };
 const [counts, setCounts] = useState({});
 // Map<isoDate, {day: bool, evening: bool, all: bool}>
 const [blocks, setBlocks] = useState(new Map());
 const [loading, setLoading] = useState(false);

 const monthKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;

 const fetchMonth = useCallback(async (mk) => {
 setLoading(true);
 try {
 const r = await fetch(`${API}/api/availability?month=${mk}`);
 const j = await r.json();
 setCounts(j.counts || {});
 } catch (e) {
 setCounts({});
 } finally {
 setLoading(false);
 }
 }, []);

 const fetchBlocked = useCallback(async () => {
 try {
 const r = await fetch(`${API}/api/availability/blocked`);
 const j = await r.json();
 const map = new Map();
 const list = Array.isArray(j.blocks) ? j.blocks : (Array.isArray(j.dates) ? j.dates.map((d) => ({ date: d, slot: "all" })) : []);
 for (const b of list) {
  const iso = b.date;
  const s = (b.slot || "all").toLowerCase();
  if (!iso) continue;
  const prev = map.get(iso) || { day: false, evening: false, all: false };
  if (s === "all") prev.all = true;
  else if (s === "day") prev.day = true;
  else if (s === "evening") prev.evening = true;
  map.set(iso, prev);
 }
 setBlocks(map);
 } catch (e) { setBlocks(new Map()); }
 }, []);

 useEffect(() => { fetchMonth(monthKey); }, [monthKey, fetchMonth]);
 useEffect(() => { fetchBlocked(); }, [fetchBlocked]);

 const grid = useMemo(() => buildGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

 const goPrev = () => {
 const next = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
 const limit = new Date(today.getFullYear(), today.getMonth(), 1);
 if (next < limit) return;
 setCursor(next);
 };
 const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));

 const selISO = selected ? toISO(selected) : null;
 const selBadge = selected ? getPopularBadge(selected) : null;
 const selBlockInfo = selISO ? (blocks.get(selISO) || { day: false, evening: false, all: false }) : { day: false, evening: false, all: false };
 const selFullyBlocked = selBlockInfo.all;
 const selPartial = !selBlockInfo.all && (selBlockInfo.day || selBlockInfo.evening);
 const selCount = selISO ? (counts[selISO] || 0) : 0;
 const selStatus = selFullyBlocked ? UNAVAILABLE_STATUS : (selPartial ? PARTIAL_STATUS : statusFor(selCount));

 const [reserveOpen, setReserveOpen] = useState(false);
 const openReserve = () => { if (selISO && !selFullyBlocked) setReserveOpen(true); };

 const canGoPrev = (() => {
 const next = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
 const limit = new Date(today.getFullYear(), today.getMonth(), 1);
 return next >= limit;
 })();

 return (
 <div className={`bg-white rounded-2xl border border-[hsla(220,20%,82%,0.6)] shadow-xl overflow-hidden ${compact ? "" : ""}`} data-testid="availability-calendar">
 {/* Header */}
 <div className="bg-gradient-to-br from-[hsl(220,60%,12%)] via-[hsl(220,55%,18%)] to-[hsl(280,50%,22%)] px-6 lg:px-8 py-6 text-white">
 <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-bold text-yellow-300" data-testid="availability-eyebrow">
 <Sparkles className="w-3.5 h-3.5" /> Live Availability
 </div>
 <div className="mt-2 flex items-end justify-between gap-4 flex-wrap">
 <div>
 <h3 className="text-white text-3xl lg:text-5xl font-black leading-[1.05] tracking-tight">Check your date.</h3>
 <p className="text-white/80 text-sm mt-2 max-w-md">Real-time availability, pick a date &amp; time slot for your Sydney Harbour cruise.</p>
 </div>
 <div className="flex items-center gap-2">
 <button onClick={goPrev} disabled={!canGoPrev} aria-label="Previous month" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
 <div className="text-white text-xl font-bold min-w-[180px] text-center" data-testid="cal-month-label">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
 <button onClick={goNext} aria-label="Next month" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
 </div>
 </div>
 </div>

 {/* Grid */}
 <div className="p-6 lg:p-8">
 <div className="grid grid-cols-7 gap-1.5 mb-2">
 {DAYS.map((d, i) => (
 <div key={i} className="text-center text-[10px] uppercase tracking-wider font-bold text-ink-mute py-1">{d}</div>
 ))}
 </div>
 <div className="grid grid-cols-7 gap-1.5" data-testid="cal-grid">
 {grid.map((date, idx) => {
 if (!date) return <div key={idx} className="aspect-square" />;
 const iso = toISO(date);
 const past = date < today;
 const isToday = iso === toISO(today);
 const isSelected = selISO === iso;
 const blockInfo = blocks.get(iso) || { day: false, evening: false, all: false };
 const isFullyBlocked = blockInfo.all;
 const isPartialBlocked = !blockInfo.all && (blockInfo.day || blockInfo.evening);
 const badge = getPopularBadge(date);
 const cnt = counts[iso] || 0;
 const status = isFullyBlocked ? UNAVAILABLE_STATUS : (isPartialBlocked ? PARTIAL_STATUS : statusFor(cnt));
 const popularCls = badge && !isFullyBlocked ? POPULAR_BADGE_CLASSES[badge.id] : null;

 const base = "relative aspect-square rounded-lg flex flex-col items-center justify-center text-sm font-bold transition-all duration-150";
 let cls;
 if (past) cls = "bg-[hsla(220,15%,95%,0.6)] text-ink-mute/40 cursor-not-allowed line-through";
 else if (isFullyBlocked) cls = "bg-zinc-200/80 text-zinc-500 cursor-not-allowed line-through ring-1 ring-zinc-400/50";
 else if (isSelected) cls = "bg-[hsl(var(--ink))] text-white shadow-lg scale-105 ring-2 ring-yellow-400";
 else if (isPartialBlocked) cls = "bg-amber-50 text-ink cursor-pointer ring-1 ring-amber-400/60 hover:bg-amber-100";
 else if (popularCls) cls = `${popularCls.cell} text-ink cursor-pointer`;
 else cls = "bg-[hsla(220,15%,96%,0.9)] hover:bg-[hsl(var(--pink))]/15 text-ink cursor-pointer";

 const disabled = past || isFullyBlocked;

 return (
 <button
 key={idx}
 type="button"
 disabled={disabled}
 onClick={() => !disabled && setSelected(date)}
 className={`${base} ${cls}`}
 data-testid={`cal-day-${iso}`}
 aria-label={`${iso}${isFullyBlocked ? " unavailable" : isPartialBlocked ? ` ${blockInfo.day ? "day cruise unavailable" : "evening cruise unavailable"}` : badge ? " " + badge.label : ""} ${status.label}`}
 >
 <span className={`${isToday && !isSelected && !isFullyBlocked ? "underline decoration-yellow-400 decoration-2 underline-offset-2" : ""}`}>{date.getDate()}</span>
 {/* Blocked icon */}
 {isFullyBlocked && !past && (
 <Ban className="absolute top-1 right-1 w-3 h-3 text-zinc-500" data-testid={`cal-blocked-${iso}`} />
 )}
 {/* Partial-block indicators — which slot is blocked */}
 {isPartialBlocked && !past && (
 <div className="absolute top-1 right-1 flex items-center gap-0.5" data-testid={`cal-partial-${iso}`}>
  {blockInfo.day && <span className="w-1.5 h-1.5 rounded-full bg-amber-600" title="Day cruise unavailable" />}
  {blockInfo.evening && <span className="w-1.5 h-1.5 rounded-full bg-indigo-600" title="Evening cruise unavailable" />}
 </div>
 )}
 {/* Popular badge: sparkle pill top-right (only when not blocked) */}
 {badge && !past && !isFullyBlocked && !isPartialBlocked && (
 <span className={`absolute -top-1 -right-1 ${popularCls.pill} rounded-full w-5 h-5 flex items-center justify-center shadow-md`} data-testid={`cal-popular-${iso}`}>
 <Sparkles className="w-2.5 h-2.5" />
 </span>
 )}
 {/* Indicator dot at bottom */}
 {!past && !isFullyBlocked && !isPartialBlocked && (
 badge ? (
 <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${popularCls.dot}`} />
 ) : cnt > 0 ? (
 <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${cnt >= 3 ? "bg-rose-500" : "bg-amber-500"}`} />
 ) : null
 )}
 </button>
 );
 })}
 </div>

 {/* Legend */}
 <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-ink-soft" data-testid="cal-legend">
 <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500" /> Available</div>
 <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500" /> Limited</div>
 <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-rose-500" /> Fully booked</div>
 <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-100 ring-1 ring-amber-400/60" /> One slot only</div>
 <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-zinc-300 ring-1 ring-zinc-400" /> Unavailable</div>
 <div className="flex items-center gap-1.5">
 <span className="relative inline-flex w-3 h-3 rounded-full bg-gradient-to-r from-fuchsia-500 to-amber-400 items-center justify-center">
 <Sparkles className="w-2 h-2 text-white" />
 </span>
 Popular date
 </div>
 {loading && <div className="text-ink-mute italic">Loading…</div>}
 </div>

 {/* Selected info */}
 {selected ? (
 <div className="mt-7" data-testid="cal-selected-panel">
 {selBadge && (
 <div className={`${POPULAR_BADGE_CLASSES[selBadge.id].banner} rounded-xl px-5 py-4 mb-4 flex items-start gap-3 shadow-md`} data-testid="cal-popular-banner">
 <Sparkles className="w-5 h-5 shrink-0 mt-0.5" />
 <div>
 <div className="font-display text-xl leading-tight">{selBadge.label}</div>
 <div className="text-[13px] opacity-95 mt-1">{selBadge.blurb} <strong>Secure this date early.</strong></div>
 </div>
 </div>
 )}

 <div className="flex flex-wrap items-center gap-3 justify-between">
 <div>
 <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-ink-mute">Your date</div>
 <div className="font-display text-2xl text-ink mt-1">
 {selected.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
 </div>
 </div>
 <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${selStatus.cls}`} data-testid="cal-status-pill">
 <selStatus.icon className="w-3.5 h-3.5" />
 {selStatus.label}
 </div>
 </div>

 {/* Partial-slot info: show which cruise is still available */}
 {selPartial && (
 <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3.5 text-sm text-amber-950" data-testid="cal-partial-msg">
 <div className="font-bold flex items-center gap-1.5 text-amber-900"><AlertCircle className="w-4 h-4" /> Limited slots on this date</div>
 <div className="mt-2 grid grid-cols-2 gap-2 text-[13px]">
 <div className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 ${selBlockInfo.day ? "bg-zinc-100 text-zinc-500 line-through" : "bg-emerald-100 text-emerald-900 font-bold"}`}>
  <Sun className="w-4 h-4" /> Day cruise · 12–4pm {selBlockInfo.day ? "· Unavailable" : "· Available"}
 </div>
 <div className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 ${selBlockInfo.evening ? "bg-zinc-100 text-zinc-500 line-through" : "bg-emerald-100 text-emerald-900 font-bold"}`}>
  <Moon className="w-4 h-4" /> Evening cruise · 6–10pm {selBlockInfo.evening ? "· Unavailable" : "· Available"}
 </div>
 </div>
 </div>
 )}

 <button
 type="button"
 onClick={openReserve}
 disabled={selFullyBlocked}
 className={`btn-yellow mt-5 w-full sm:w-auto justify-center ${hideCTA || selFullyBlocked ? "hidden" : ""}`}
 data-testid="cal-check-booking-btn"
 >
 <ShieldCheck className="w-4 h-4" />
 Reserve this date · $1,000 deposit
 </button>
 {selFullyBlocked && (
 <div className="mt-5 rounded-xl border border-zinc-300 bg-zinc-100 px-4 py-3 text-sm text-zinc-700 flex items-start gap-2.5" data-testid="cal-unavailable-msg">
 <Ban className="w-4 h-4 mt-0.5 shrink-0 text-zinc-500" />
 <div>
 <div className="font-bold">This date is unavailable.</div>
 <div className="text-zinc-600 text-[13px] mt-0.5">The Magic isn&apos;t sailing on this date. Please choose another date from the calendar.</div>
 </div>
 </div>
 )}
 {!selFullyBlocked && (
 <p className="text-[11px] text-ink-mute mt-2">$1,000 non-refundable deposit secures your date. Applied as credit toward your final cruise total. Full payment options provided at checkout.</p>
 )}
 </div>
 ) : (
 <div className="mt-7 rounded-xl border border-dashed border-[hsla(220,20%,82%,0.8)] px-5 py-6 text-center text-ink-mute text-sm" data-testid="cal-empty-state">
 Pick a date above to see live availability and lock it in with a $1,000 holding deposit.
 </div>
 )}
 </div>

 {/* Holding-deposit modal — opens with the selected date pre-filled */}
 <ReserveDateModal
  open={reserveOpen}
  onClose={() => setReserveOpen(false)}
  cruiseType="private-charter"
  cruiseLabel="Private Charter"
  accent="bg-amber-500 hover:bg-amber-400"
  initialDate={selISO || ""}
 />
 </div>
 );
}
